/**
 * Sarvam Studio — unified UI for all 6 Sarvam AI capabilities.
 * Route: /tools/sarvam-studio
 */
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mic,
  Volume2,
  Languages,
  Type,
  Search,
  MessageCircle,
  Upload,
  Sparkles,
  Loader2,
  Copy,
  Download,
  Play,
  RefreshCw,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/sarvam`;

const TABS = [
  { id: "stt", label: "Speech → Text", Icon: Mic, desc: "22 Indian languages · Studio-grade accuracy" },
  { id: "tts", label: "Text → Speech", Icon: Volume2, desc: "40+ Indian voices · natural narration" },
  { id: "translate", label: "Translate", Icon: Languages, desc: "23 languages · 4 tone modes" },
  { id: "transliterate", label: "Transliterate", Icon: Type, desc: "Script conversion (Roman ⇄ Devanagari)" },
  { id: "lid", label: "Language ID", Icon: Search, desc: "Auto-detect any language" },
  { id: "chat", label: "Indic Chat", Icon: MessageCircle, desc: "Frontier Indic LLM" },
];

const LANGS = [
  { code: "hi-IN", label: "Hindi (हिन्दी)" },
  { code: "en-IN", label: "English (India)" },
  { code: "ta-IN", label: "Tamil (தமிழ்)" },
  { code: "te-IN", label: "Telugu (తెలుగు)" },
  { code: "bn-IN", label: "Bengali (বাংলা)" },
  { code: "gu-IN", label: "Gujarati (ગુજરાતી)" },
  { code: "kn-IN", label: "Kannada (ಕನ್ನಡ)" },
  { code: "ml-IN", label: "Malayalam (മലയാളം)" },
  { code: "mr-IN", label: "Marathi (मराठी)" },
  { code: "pa-IN", label: "Punjabi (ਪੰਜਾਬੀ)" },
  { code: "od-IN", label: "Odia (ଓଡ଼ିଆ)" },
];

const STT_LANGS = [
  { code: "auto", label: "Auto-detect" },
  { code: "hi", label: "Hindi (हिन्दी)" },
  { code: "hinglish", label: "Hinglish (Roman)" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "bn", label: "Bengali" },
  { code: "gu", label: "Gujarati" },
  { code: "kn", label: "Kannada" },
  { code: "ml", label: "Malayalam" },
  { code: "mr", label: "Marathi" },
  { code: "pa", label: "Punjabi" },
  { code: "or", label: "Odia" },
  { code: "as", label: "Assamese" },
  { code: "ur", label: "Urdu" },
  { code: "en", label: "English" },
];

const STT_MODES = [
  { code: "transcribe", label: "Transcribe (native script)" },
  { code: "translate", label: "Translate → English" },
  { code: "translit", label: "Transliterate → Roman (Hinglish)" },
  { code: "codemix", label: "Code-mixed (native + English)" },
  { code: "verbatim", label: "Verbatim (with fillers)" },
];

const TRANSLATE_MODES = [
  { code: "formal", label: "Formal" },
  { code: "modern-colloquial", label: "Modern colloquial" },
  { code: "classic-colloquial", label: "Classic colloquial" },
  { code: "code-mixed", label: "Code-mixed" },
];

const SPEAKERS = ["shreya", "priya", "kavitha", "niharika", "simran", "ritu", "rupali", "shruti", "kavya", "ishita", "roopa", "tanya", "suhani", "pooja", "neha", "aditya", "ashutosh", "rahul", "rohan", "amit", "dev", "kabir", "shubh", "advait", "anand", "vijay", "mohit"];

function copyText(t) {
  navigator.clipboard.writeText(t);
  toast.success("Copied!");
}

// ── STT panel ────────────────────────────────────────────────────────
function SttPanel() {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("transcribe");
  const [language, setLanguage] = useState("auto");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!file) { toast.error("Select an audio file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("language", language);
      const res = await axios.post(`${API}/stt`, fd);
      setResult(res.data.result);
      toast.success("Transcribed!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcription failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-neutral-600">
          Language
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="sarvam-stt-lang">
            {STT_LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          Output mode
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="sarvam-stt-mode">
            {STT_MODES.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
          </select>
        </label>
      </div>

      <label className="block border-2 border-dashed border-neutral-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#A855F7] transition-colors">
        <input type="file" accept=".mp3,.m4a,.wav,.mp4,.webm,.ogg,.flac" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="sarvam-stt-file" />
        <Upload className="w-8 h-8 mx-auto text-neutral-400 mb-2" />
        <div className="text-sm font-semibold text-neutral-700">{file ? file.name : "Click to upload audio"}</div>
        <div className="text-xs text-neutral-500 mt-1">MP3 · WAV · M4A · MP4 · up to 100 MB</div>
      </label>

      <Button onClick={run} disabled={busy || !file} className="w-full bg-[#111111] hover:bg-[#333] text-white h-11" data-testid="sarvam-stt-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcribing…</> : "Transcribe"}
      </Button>

      {result && (
        <Card className="p-5 border-neutral-200 space-y-3">
          <div className="flex items-center justify-between">
            <Badge className="bg-emerald-50 text-emerald-700 border-transparent">
              {result.language} · {result.duration?.toFixed(1)}s · {result.words?.length || 0} words
            </Badge>
            <Button variant="outline" size="sm" onClick={() => copyText(result.text)} data-testid="sarvam-stt-copy">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
          <p className="text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap" data-testid="sarvam-stt-result">{result.text}</p>
        </Card>
      )}
    </div>
  );
}

// ── TTS panel ────────────────────────────────────────────────────────
function TtsPanel() {
  const [text, setText] = useState("नमस्ते! मैं MarkeeMix हूं — आपका AI workspace. आज हम बात करते हैं SEO और content marketing के बारे में.");
  const [language, setLanguage] = useState("hi-IN");
  const [speaker, setSpeaker] = useState("shreya");
  const [pace, setPace] = useState(1.0);
  const [busy, setBusy] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [voices, setVoices] = useState([]);
  const [previewSpeaker, setPreviewSpeaker] = useState("");

  useEffect(() => {
    axios.get(`${API}/tts/voices`).then(r => setVoices(r.data?.voices || [])).catch(() => {});
  }, []);

  const playPreview = (spk) => {
    setPreviewSpeaker(spk);
    const audio = new Audio(`${API}/tts/preview/${spk}?lang=${language}`);
    audio.play().catch(() => {
      // Fallback to hi-IN
      new Audio(`${API}/tts/preview/${spk}?lang=hi-IN`).play().catch(() => {});
    });
    audio.onended = () => setPreviewSpeaker("");
  };

  const run = async () => {
    if (!text.trim()) { toast.error("Type some text first"); return; }
    setBusy(true); setAudioUrl("");
    try {
      const res = await axios.post(`${API}/tts`,
        { text, language_code: language, speaker, pace },
        { responseType: "blob" }
      );
      setAudioUrl(URL.createObjectURL(res.data));
      toast.success("Voice generated!");
    } catch (e) {
      toast.error("TTS failed");
    } finally { setBusy(false); }
  };

  const currentGender = voices.find(v => v.id === speaker)?.gender;

  return (
    <div className="space-y-4">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder="Type in Hindi, Tamil, Telugu, Bengali, or any Indian language…"
        maxLength={8000}
        data-testid="sarvam-tts-text"
      />
      <div className="grid grid-cols-3 gap-3">
        <label className="text-xs font-semibold text-neutral-600 col-span-1">
          Language
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="sarvam-tts-lang">
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-neutral-600 col-span-1">
          Voice
          <select value={speaker} onChange={(e) => setSpeaker(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm capitalize" data-testid="sarvam-tts-speaker">
            {SPEAKERS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-neutral-600 col-span-1">
          Pace ({pace.toFixed(2)}x)
          <input type="range" min="0.5" max="2" step="0.05" value={pace} onChange={(e) => setPace(parseFloat(e.target.value))} className="mt-3 w-full" data-testid="sarvam-tts-pace" />
        </label>
      </div>

      {/* Voice gallery with click-to-preview */}
      {voices.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-neutral-600 mb-2 flex items-center justify-between">
            <span>Preview a voice</span>
            <span className="text-[10px] text-neutral-400 font-normal">Click any to hear a sample</span>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 max-h-40 overflow-y-auto p-1 border border-neutral-200 rounded-lg" data-testid="sarvam-tts-voice-grid">
            {voices.map(v => {
              const active = v.id === speaker;
              const playing = v.id === previewSpeaker;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => { setSpeaker(v.id); playPreview(v.id); }}
                  className={`relative px-2 py-2 rounded-md text-[11px] font-semibold capitalize transition-all ${
                    active
                      ? "bg-[#A855F7] text-white shadow"
                      : "bg-neutral-50 text-neutral-700 hover:bg-neutral-100"
                  }`}
                  data-testid={`sarvam-tts-voice-${v.id}`}
                >
                  <span className="flex items-center justify-center gap-1">
                    {playing ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    ) : (
                      <Play className="w-2.5 h-2.5" />
                    )}
                    {v.id}
                  </span>
                  <span className={`text-[9px] block mt-0.5 ${active ? "text-white/70" : "text-neutral-400"}`}>
                    {v.gender === "female" ? "♀" : "♂"}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-neutral-400 mt-1">
            Selected: <span className="capitalize font-semibold text-neutral-700">{speaker}</span>
            {currentGender && <span className="ml-1">({currentGender})</span>}
          </p>
        </div>
      )}

      <Button onClick={run} disabled={busy || !text.trim()} className="w-full bg-[#111111] hover:bg-[#333] text-white h-11" data-testid="sarvam-tts-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating voice…</> : "Generate voice"}
      </Button>

      {audioUrl && (
        <Card className="p-4 border-neutral-200 flex items-center gap-3">
          <Play className="w-5 h-5 text-[#A855F7]" />
          <audio controls src={audioUrl} className="flex-1" data-testid="sarvam-tts-audio" />
          <a href={audioUrl} download="narration.wav" className="text-sm font-semibold text-[#7C3AED] hover:underline">
            <Download className="w-4 h-4 inline mr-1" /> Save
          </a>
        </Card>
      )}
    </div>
  );
}

// ── Translate panel ──────────────────────────────────────────────────
function TranslatePanel() {
  const [text, setText] = useState("");
  const [source, setSource] = useState("en-IN");
  const [target, setTarget] = useState("hi-IN");
  const [mode, setMode] = useState("formal");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);

  const SOURCES = [{ code: "auto", label: "Auto-detect" }, ...LANGS];

  const run = async () => {
    if (!text.trim()) return;
    setBusy(true); setResult("");
    try {
      let effectiveSource = source;
      // If Auto-detect, first call LID to resolve the real BCP-47 code
      if (source === "auto") {
        try {
          const lid = await axios.post(`${API}/identify-language`, { input: text.slice(0, 500) });
          effectiveSource = lid.data?.result?.language_code || "en-IN";
        } catch {
          effectiveSource = "en-IN";
        }
      }
      const res = await axios.post(`${API}/translate`, {
        input: text, source_language_code: effectiveSource, target_language_code: target, mode,
      });
      setResult(res.data.result.translated_text || "");
      toast.success("Translated!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Text to translate…" maxLength={2000} data-testid="sarvam-tr-text" />
      <div className="grid grid-cols-3 gap-3">
        <label className="text-xs font-semibold text-neutral-600">
          From
          <select value={source} onChange={(e) => setSource(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="sarvam-tr-src">
            {SOURCES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          To
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="sarvam-tr-tgt">
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          Mode
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="sarvam-tr-mode">
            {TRANSLATE_MODES.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
          </select>
        </label>
      </div>
      <Button onClick={run} disabled={busy || !text.trim()} className="w-full bg-[#111111] hover:bg-[#333] text-white h-11" data-testid="sarvam-tr-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Translating…</> : "Translate"}
      </Button>
      {result && (
        <Card className="p-5 border-neutral-200 space-y-2">
          <div className="flex items-center justify-between">
            <Badge className="bg-emerald-50 text-emerald-700 border-transparent">Translated to {target}</Badge>
            <Button variant="outline" size="sm" onClick={() => copyText(result)}>
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
          <p className="text-lg text-neutral-800 leading-relaxed" data-testid="sarvam-tr-result">{result}</p>
        </Card>
      )}
    </div>
  );
}

// ── Transliterate panel ─────────────────────────────────────────────
function TransliteratePanel() {
  const [text, setText] = useState("");
  const [source, setSource] = useState("en-IN");
  const [target, setTarget] = useState("hi-IN");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!text.trim()) return;
    setBusy(true); setResult("");
    try {
      const res = await axios.post(`${API}/transliterate`, {
        input: text, source_language_code: source, target_language_code: target,
      });
      setResult(res.data.result.transliterated_text || "");
    } catch (e) {
      toast.error("Transliteration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder='Try "namaste kaise ho" or "नमस्ते"…' maxLength={1000} data-testid="sarvam-xl-text" />
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-neutral-600">
          From
          <select value={source} onChange={(e) => setSource(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="sarvam-xl-src">
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          To
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="sarvam-xl-tgt">
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
      </div>
      <Button onClick={run} disabled={busy || !text.trim()} className="w-full bg-[#111111] hover:bg-[#333] text-white h-11" data-testid="sarvam-xl-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Converting…</> : "Transliterate"}
      </Button>
      {result && (
        <Card className="p-5 border-neutral-200">
          <p className="text-lg text-neutral-800 leading-relaxed" data-testid="sarvam-xl-result">{result}</p>
        </Card>
      )}
    </div>
  );
}

// ── LID panel ────────────────────────────────────────────────────────
function LidPanel() {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!text.trim()) return;
    setBusy(true); setResult(null);
    try {
      const res = await axios.post(`${API}/identify-language`, { input: text });
      setResult(res.data.result);
    } catch (e) {
      toast.error("LID failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Paste any text — we'll tell you the language & script." data-testid="sarvam-lid-text" />
      <Button onClick={run} disabled={busy || !text.trim()} className="w-full bg-[#111111] hover:bg-[#333] text-white h-11" data-testid="sarvam-lid-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Detecting…</> : "Identify language"}
      </Button>
      {result && (
        <Card className="p-5 border-neutral-200 flex gap-4">
          <div>
            <div className="text-xs text-neutral-500 uppercase font-semibold">Language</div>
            <div className="font-heading text-2xl font-semibold text-[#111111] mt-1" data-testid="sarvam-lid-lang">{result.language_code}</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500 uppercase font-semibold">Script</div>
            <div className="font-heading text-2xl font-semibold text-[#111111] mt-1">{result.script_code}</div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Chat panel ──────────────────────────────────────────────────────
function ChatPanel() {
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!prompt.trim()) return;
    const nextMessages = [...messages, { role: "user", content: prompt }];
    setMessages(nextMessages); setPrompt(""); setBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        messages: nextMessages, temperature: 0.7, max_tokens: 1024,
      });
      const reply = res.data.result?.choices?.[0]?.message?.content
        || res.data.result?.choices?.[0]?.message?.reasoning_content
        || "(No response)";
      setMessages([...nextMessages, { role: "assistant", content: reply }]);
    } catch (e) {
      toast.error("Chat failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3 max-h-96 overflow-y-auto p-1" data-testid="sarvam-chat-history">
        {messages.length === 0 && (
          <div className="text-center py-10 text-neutral-400 text-sm">
            Chat with our frontier Indic reasoning model.<br />
            Try: &quot;10 Instagram post ideas for a Diwali sweets shop in Hindi&quot;
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`p-3 rounded-lg ${m.role === "user" ? "bg-neutral-100 ml-8" : "bg-[#A855F7]/5 mr-8 border border-[#A855F7]/20"}`}>
            <div className="text-[10px] uppercase font-bold text-neutral-500 mb-1">{m.role === "user" ? "You" : "MarkeeMix Indic"}</div>
            <div className="text-sm text-neutral-800 whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Ask anything in Hindi, English, or any Indian language…" disabled={busy} data-testid="sarvam-chat-input" />
        <Button onClick={run} disabled={busy || !prompt.trim()} className="bg-[#111111] hover:bg-[#333] text-white" data-testid="sarvam-chat-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
        </Button>
      </div>
    </div>
  );
}

export default function SarvamStudioPage() {
  const [tab, setTab] = useState("stt");
  const [health, setHealth] = useState(null);

  useEffect(() => {
    axios.get(`${API}/health`).then(r => setHealth(r.data)).catch(() => {});
  }, []);

  const ActivePanel = {
    stt: SttPanel,
    tts: TtsPanel,
    translate: TranslatePanel,
    transliterate: TransliteratePanel,
    lid: LidPanel,
    chat: ChatPanel,
  }[tab];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <PremiumNav />

      {/* Hero */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-orange-100 text-orange-800 border-transparent">🇮🇳 Made for India</Badge>
            <Badge className="bg-[#A855F7]/10 text-[#7C3AED] border-transparent">MarkeeMix Indic AI</Badge>
            {health?.configured && <Badge className="bg-emerald-100 text-emerald-800 border-transparent">Live</Badge>}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#111111] tracking-tight">
            MarkeeMix Indic AI Studio
          </h1>
          <p className="text-neutral-600 mt-3 max-w-2xl text-base">
            Speech-to-text in 22 Indian languages · 40+ natural voices · High-accuracy
            translation · Language identification · Roman ⇄ Devanagari transliteration ·
            Frontier Indic chat — all inside MarkeeMix.
          </p>
        </div>
      </section>

      {/* Tab strip */}
      <div className="border-b border-neutral-200 bg-white sticky top-16 z-10">
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.Icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-4 border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
                  active
                    ? "border-[#A855F7] text-[#7C3AED]"
                    : "border-transparent text-neutral-500 hover:text-neutral-800"
                }`}
                data-testid={`sarvam-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-semibold">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> {TABS.find(t => t.id === tab)?.desc}
          </div>
        </div>
        <Card className="p-6 border-neutral-200 bg-white">
          <ActivePanel />
        </Card>

        <div className="mt-8 text-center text-xs text-neutral-500">
          Part of the <span className="text-[#7C3AED] font-semibold">MarkeeMix Indic AI</span> stack · 256K context · Studio-grade Indic voice tech
        </div>
      </main>

      <PremiumFooter />
    </div>
  );
}
